import { TypeChecker } from '@attain-apps/type-guards'
import _uniq from 'lodash-es/uniq'

import type { PotentiallyOptional } from '../../interfaces'
import { Raw } from '../raw'

import { Intrinsic } from './intrinsic'
import type { Type } from './type'

// TODO: maybe we replace this with some sort of system that uses JSON schemas.
// That way we provide much more flexibility, while implementing far less logic
// ourselves

/**
 * Represents an {@link Intrinsic.Object} or {@link Intrinsic.List} with at
 * least some known properties
 */
export class Structure<T = any> {
  /**
   * Represents an indefinite length list of a given {@link Type}
   */
  public static list<TType extends Type>(
    type: TType,
  ): Structure<readonly Raw<TType>[]>
  public static list<T>(type: Type<T>): Structure<readonly T[]>
  public static list<T>(type: Type<T>): Structure<readonly T[]> {
    return new Structure<readonly T[]>(
      {
        known: [],
        others: type,
      },
      Intrinsic.List,
    )
  }

  /**
   * Represents a list of a defined length with a specific {@link Type} for
   * each position
   */
  public static tuple<const T extends readonly unknown[]>(
    known: Structure.Properties.Known<T>,
  ): Structure<T> {
    return new Structure<T>({ known }, Intrinsic.List)
  }

  /**
   * Represents an object which can contain known propreties each with a
   * defined {@link Type}, as well as a {@link Type} for all other (unknown)
   * properties
   */
  public static object<T extends Object.Constraint>(
    properties: Structure.Properties<T>,
  ): Structure<T> {
    return new Structure<T>(properties, Intrinsic.Object)
  }

  /**
   * Indicates the {@link Structure} has at least one known property
   */
  public readonly hasKnownProperties: boolean

  /**
   * Indicates the {@link Structure} can accept additional properties besides
   * the known properties
   */
  public readonly supportsUnknownProperties: boolean

  /**
   * All {@link Type} values that appear in properties of this
   * {@link Structure}
   */
  public readonly allPropertyTypes: readonly Type[]

  /**
   * Checks if a given value matches the type represented by this
   * {@link Structure}
   */
  public get accepts(): TypeChecker<T> {
    if (this._accepts) {
      // If we have already created the type checker, we reuse it
      return this._accepts
    }
    // Otherwise, we create it
    if (this.intrinsic === Intrinsic.List) {
      const knownPropertyTypeCheckers = Object.values<
        Structure.Property | undefined
      >(this.properties.known)
        .filter(TypeChecker.isNotNullish.raw)
        .map(property => Raw.getTypeCheckerFor(property.type))
      return (this._accepts = (this.properties.others
        ? TypeChecker.Factory.list({
            endsWith: Raw.getTypeCheckerFor(this.properties.others),
            startsWith: knownPropertyTypeCheckers,
          })
        : TypeChecker.Factory.tuple(
            ...knownPropertyTypeCheckers,
          )) satisfies TypeChecker<readonly unknown[]> as TypeChecker<T>)
    } else {
      // NOTE: because we are creating this dynamically, we need to type
      // these things pretty loosely
      const fieldCheckers: TypeChecker.Factory.object.Fields<
        Record<string, unknown>
      > = {}
      let hasKnownProperty = false
      for (const k in this.properties.known) {
        const property =
          this.properties.known[k as keyof typeof this.properties.known]
        if (!property) {
          continue
        }
        const typeChecker = Raw.getTypeCheckerFor(property.type)
        fieldCheckers[k] = property.isOptional
          ? TypeChecker.Factory.optional(typeChecker)
          : typeChecker
        hasKnownProperty = true
      }
      const knownPropertiesTypeChecker = hasKnownProperty
        ? TypeChecker.Factory.object(
            fieldCheckers as TypeChecker.Factory.object.Fields<
              Extract<T, object>
            >,
          )
        : undefined
      const otherPropertyTypeChecker = this.properties.others
        ? Raw.getTypeCheckerFor(this.properties.others)
        : undefined
      const otherPropertiesTypeChecker = otherPropertyTypeChecker
        ? TypeChecker.Factory.custom(
            (val: unknown): val is Record<string, unknown> => {
              if (!TypeChecker.isObject(val)) {
                return false
              }
              for (const k in val) {
                if (
                  this.properties.known[k as keyof typeof this.properties.known]
                ) {
                  // If this is a known property, it is not meant to be checked here
                  continue
                }
                if (!otherPropertyTypeChecker(val[k as keyof typeof val])) {
                  // If this is not a known property and it doesn't match the type for other properties, then the object is invalid
                  return false
                }
              }
              // If all properties pass, then the type is valid
              return true
            },
            {
              typeDescription: `Record<OtherProperty, ${otherPropertyTypeChecker.typeDescription}>`,
            },
          )
        : undefined
      return (this._accepts = TypeChecker.Factory.intersection(
        ...[knownPropertiesTypeChecker, otherPropertiesTypeChecker].filter(
          TypeChecker.isNotNullish.raw,
        ),
      ))
    }
  }

  /**
   * Since creating {@link accepts} is non-trivial effort with a fair amount
   * of iteration and recursion, we only want to execute that if needed.
   * However, if it is needed, we only want to execute it once. Thus,
   * {@link accepts} is a getter that returns this value if it is defined, or
   * else defines it and returns it so that it can be reused next time
   */
  private _accepts: TypeChecker<T> | undefined

  private constructor(
    /**
     * TODO:
     */
    public readonly properties: Structure.Properties<T>,
    public readonly intrinsic: Intrinsic,
  ) {
    this.hasKnownProperties = Object.values(properties.known).some(
      property => !!property,
    )
    this.supportsUnknownProperties = !!properties.others
    this.allPropertyTypes = _uniq(
      Object.values<Structure.Property | undefined>(properties.known)
        .filter(TypeChecker.isNotNullish.raw)
        .map(property => property.type)
        .concat(properties.others ?? []),
    )
  }

  /**
   * Returns the {@link Structure.Property} of the given property, if
   * determinable
   */
  public getProperty(key: Key.In.Union<T>): Structure.Property | undefined {
    return (
      this.properties.known[key] ??
      // If this is not a known property, then we see if we have a type for
      // unknown properties, and if so, we create the property object
      (this.properties.others
        ? {
            isOptional: true,
            type: this.properties.others,
          }
        : undefined)
    )
  }
}
export namespace Structure {
  export const isStructure: TypeChecker<Structure> =
    TypeChecker.Factory.instance(
      Structure as unknown as new () => Structure,
    ).usingAlias('common.value.types.Structure')

  /**
   * TODO:
   */
  export type Property<T = any> = {
    type: Type<T>
  } & PotentiallyOptional

  /**
   * TODO:
   */
  export interface Properties<T = any> {
    /**
     * Represents the {@link Type} of each known {@link Property}
     */
    readonly known: Properties.Known<T>
    /**
     * Represents the {@link Type} of all unknown properties
     */
    readonly others?: Type
  }
  export namespace Properties {
    /**
     * TODO:
     */
    export type Known<T = any> = AllowMissing.IgnoringAny<{
      readonly [K in keyof T]:
        | Property<T[K]>
        | (K extends Generalize<K> ? undefined : never)
    }>
  }
}
